import { useEffect, useRef, useState } from "react";
import { api, API, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Image as ImageIcon, Trash2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
    { key: "inverter_warranty", label: "Inverter Warranty" },
    { key: "panel_warranty", label: "Panel Warranty" },
    { key: "sld_diagram", label: "SLD Diagram" },
    { key: "reslink_report", label: "Reslink Report" },
    { key: "eb_details", label: "EB Details" },
    { key: "agreement_copy", label: "Agreement Copy" },
    { key: "invoice", label: "Invoice" },
];

const ACCEPT = "application/pdf,image/png,image/jpeg,image/webp";

function formatBytes(bytes) {
    if (!bytes && bytes !== 0) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" });
    } catch { return iso; }
}

export default function DocumentVault({ stationId }) {
    const { user } = useAuth();
    const canUpload = user && (user.role === "super_admin" || user.role === "sales");
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/plants/${stationId}/documents`);
            setDocs(data);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [stationId]);

    const byType = {};
    for (const d of docs) {
        byType[d.doc_type] = byType[d.doc_type] || [];
        byType[d.doc_type].push(d);
    }

    return (
        <div className="bg-white border border-zinc-200 rounded-sm" data-testid="document-vault">
            <div className="p-4 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Document Vault</div>
                </div>
                <div className="text-sm text-zinc-700 mt-0.5">
                    Secure storage for plant paperwork · PDF or Image · Max 10 MB
                </div>
            </div>

            {loading ? (
                <div className="p-6 text-sm text-zinc-500">Loading documents…</div>
            ) : (
                <div className="divide-y divide-zinc-100">
                    {DOC_TYPES.map(dt => (
                        <DocTypeRow
                            key={dt.key}
                            docType={dt}
                            stationId={stationId}
                            files={byType[dt.key] || []}
                            canUpload={canUpload}
                            onChanged={load}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function DocTypeRow({ docType, stationId, files, canUpload, onChanged }) {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onFileChange = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 10 * 1024 * 1024) {
            toast.error("File too large — max 10 MB");
            return;
        }
        const okType = ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(f.type);
        if (!okType) {
            toast.error("Only PDF, PNG, JPEG, or WEBP allowed");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", docType.key);
            fd.append("file", f);
            await api.post(`/plants/${stationId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`${docType.label} uploaded`);
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally { setUploading(false); }
    };

    const remove = async (docId) => {
        if (!window.confirm("Delete this document? This cannot be undone.")) return;
        try {
            await api.delete(`/documents/${docId}`);
            toast.success("Document deleted");
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        }
    };

    const openDoc = async (doc) => {
        // Fetch as blob so the Bearer token can be sent via axios interceptor.
        try {
            const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            window.open(url, "_blank", "noopener,noreferrer");
            // Revoke after a short delay so the tab has a chance to render
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        }
    };

    return (
        <div className="p-4" data-testid={`doc-row-${docType.key}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-sm font-medium text-zinc-900">{docType.label}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                        {files.length === 0 ? "No file uploaded" : `${files.length} file${files.length === 1 ? "" : "s"}`}
                    </div>
                </div>
                {canUpload && (
                    <>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept={ACCEPT}
                            onChange={onFileChange}
                            data-testid={`upload-input-${docType.key}`}
                        />
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="rounded-sm h-8 text-xs"
                            data-testid={`upload-btn-${docType.key}`}
                        >
                            <Upload className="w-3.5 h-3.5 mr-1.5" />
                            {uploading ? "Uploading…" : "Upload"}
                        </Button>
                    </>
                )}
            </div>
            {files.length > 0 && (
                <div className="mt-3 space-y-1.5">
                    {files.map((f, i) => (
                        <div key={f.id} className="flex items-center gap-2 text-xs bg-zinc-50 rounded-sm px-3 py-2 border border-zinc-200" data-testid={`doc-${docType.key}-${i}`}>
                            {f.content_type === "application/pdf" ? <FileText className="w-3.5 h-3.5 text-[#DC2626] flex-shrink-0" /> : <ImageIcon className="w-3.5 h-3.5 text-[#16A34A] flex-shrink-0" />}
                            <button
                                onClick={() => openDoc(f)}
                                className="text-zinc-800 font-medium hover:text-[#0A0A0A] truncate max-w-[240px]"
                                data-testid={`open-doc-${docType.key}-${i}`}
                                title={f.original_filename}
                            >
                                {f.original_filename}
                            </button>
                            <span className="text-zinc-400 font-mono ml-auto">{formatBytes(f.size)}</span>
                            <span className="text-zinc-500 font-mono">{formatDate(f.uploaded_at)}</span>
                            <button onClick={() => openDoc(f)} className="text-zinc-400 hover:text-[#0A0A0A]" title="Open">
                                <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                            {canUpload && (
                                <button
                                    onClick={() => remove(f.id)}
                                    className="text-zinc-400 hover:text-[#DC2626]"
                                    title="Delete"
                                    data-testid={`delete-doc-${docType.key}-${i}`}
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
